import type { ButtonHTMLAttributes, ReactNode } from 'react';

import { cx } from './cx';

/**
 * One button hierarchy, in one place.
 *
 * Solid brand for the single action a screen exists for, outlined for the rest, quiet — a
 * faint border, never bare text — for navigation and dismissal, outlined red for the
 * destructive one. `quiet` used to have no border at all, and *Close*, *Refresh* and
 * *7 more* were taken for labels; a button reads as a button. A screen has at most
 * one `primary` in view — that is the whole point of the hierarchy, and it is the rule a
 * second implementation of this component would break first.
 *
 * `go` is the exception, and it is narrow on purpose: solid green, for an action that has
 * to be taken before the console can do its job at all. It does not compete with `primary`
 * because it is not the screen's purpose — it is the thing standing in the way of it, and
 * it disappears the moment it is done. Green rather than the firm's accent for the same
 * reason: the accent means "this firm", and a dispatcher should not have to learn that it
 * sometimes also means "press me". Today there is exactly one, on the microphone banner.
 */
export type ButtonVariant = 'primary' | 'outline' | 'quiet' | 'danger' | 'go';

/*
 * Each variant names its own border colour, and the base names none.
 *
 * Two border-colour utilities on one element is a coin toss decided by Tailwind's own
 * ordering, not by which one is written last — a base of `border-transparent` beat every
 * variant's colour and left the outline and danger buttons with no outline at all.
 */
const VARIANTS: Record<ButtonVariant, string> = {
  primary: 'bg-brand text-on-brand border-transparent enabled:hover:brightness-110',
  outline: 'bg-transparent border-rule text-ink-2 enabled:hover:bg-surface-2',
  quiet: 'bg-transparent border-rule-soft text-ink-2 enabled:hover:bg-surface-2',
  danger: 'bg-transparent border-danger text-danger enabled:hover:bg-danger-soft',
  go: 'bg-go text-on-go border-transparent enabled:hover:brightness-110',
};

export function Button({
  variant = 'outline',
  size = 'md',
  icon,
  className,
  children,
  ...rest
}: {
  variant?: ButtonVariant;
  /** 32px is every control on a row; 26px is the one inside a card header or a table cell. */
  size?: 'md' | 'sm';
  /** A square button with nothing but an icon in it. */
  icon?: boolean;
  className?: string;
  children?: ReactNode;
} & Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'className' | 'children'>) {
  return (
    <button
      type="button"
      {...rest}
      className={cx(
        'inline-flex items-center justify-center gap-1.5 rounded-control border font-semibold whitespace-nowrap',
        'disabled:cursor-not-allowed disabled:opacity-60',
        size === 'sm' ? 'h-[26px] text-[12px]' : 'h-8 text-[13px]',
        icon
          ? size === 'sm'
            ? 'w-[26px] px-0'
            : 'w-8 px-0'
          : variant === 'quiet'
            ? 'px-2'
            : size === 'sm'
              ? 'px-2'
              : 'px-3',
        VARIANTS[variant],
        className,
      )}
    >
      {children}
    </button>
  );
}
